import { useState, useEffect, useMemo } from "react";

const LONDON_TZ = "Europe/London";

interface LondonTimeParts {
  weekday: string;
  hour: number;
  minute: number;
}

function getLondonTimeParts(date: Date): LondonTimeParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: LONDON_TZ,
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(date);

  const weekday = parts.find((p) => p.type === "weekday")!.value;
  const hour = parseInt(parts.find((p) => p.type === "hour")!.value, 10);
  const minute = parseInt(parts.find((p) => p.type === "minute")!.value, 10);

  return { weekday, hour: hour === 24 ? 0 : hour, minute };
}

function isWithinWeekendWindow(date: Date): boolean {
  const { weekday, hour, minute } = getLondonTimeParts(date);

  if (weekday === "Fri" && hour >= 22) return true;
  if (weekday === "Sat") return true;
  if (weekday === "Sun" && (hour < 21 || (hour === 21 && minute < 55)))
    return true;

  return false;
}

/**
 * Compute the next Sunday 22:00 London time as a UTC Date.
 * Only called when we're inside the weekend window.
 */
function getNextSundayReopenUTC(now: Date): Date {
  const { weekday } = getLondonTimeParts(now);

  const daysUntilSunday =
    weekday === "Fri" ? 2 : weekday === "Sat" ? 1 : 0;

  // Get London's current UTC offset by comparing formatted London date to UTC
  const londonDate = new Intl.DateTimeFormat("en-US", {
    timeZone: LONDON_TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(now);

  const lYear = parseInt(
    londonDate.find((p) => p.type === "year")!.value,
    10,
  );
  const lMonth =
    parseInt(londonDate.find((p) => p.type === "month")!.value, 10) - 1;
  const lDay = parseInt(londonDate.find((p) => p.type === "day")!.value, 10);

  // Build a "London calendar" date for Sunday 22:00 as if it were UTC
  const sundayLondonAsUTC = new Date(
    Date.UTC(lYear, lMonth, lDay + daysUntilSunday, 22, 0, 0, 0),
  );

  // Find London's UTC offset at that moment by checking what hour London shows
  const checkParts = new Intl.DateTimeFormat("en-US", {
    timeZone: LONDON_TZ,
    hour: "numeric",
    hour12: false,
  }).formatToParts(sundayLondonAsUTC);
  const londonHourAtUTC = parseInt(
    checkParts.find((p) => p.type === "hour")!.value,
    10,
  );
  const offsetHours = londonHourAtUTC === 24 ? 0 : londonHourAtUTC - 22;

  // Subtract the offset so that London reads 22:00
  return new Date(sundayLondonAsUTC.getTime() - offsetHours * 3600_000);
}

/**
 * Compute ms until the next state transition:
 * - If markets are closed → ms until Sunday 21:55 London (banner hides)
 * - If markets are open → ms until Friday 22:00 London (banner shows)
 */
function msUntilNextTransition(now: Date, isClosed: boolean): number {
  if (isClosed) {
    // Compute Sunday 21:55 London
    const sundayReopen = getNextSundayReopenUTC(now);
    // 21:55 is 5 minutes before 22:00
    const hideTime = new Date(sundayReopen.getTime() - 5 * 60_000);
    return Math.max(hideTime.getTime() - now.getTime(), 1000);
  }

  // Markets are open — find next Friday 22:00 London
  const { weekday } = getLondonTimeParts(now);
  const dayMap: Record<string, number> = {
    Sun: 5,
    Mon: 4,
    Tue: 3,
    Wed: 2,
    Thu: 1,
    Fri: 0,
    Sat: 6,
  };
  const daysUntilFriday = dayMap[weekday] ?? 0;

  const londonDate = new Intl.DateTimeFormat("en-US", {
    timeZone: LONDON_TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(now);

  const lYear = parseInt(
    londonDate.find((p) => p.type === "year")!.value,
    10,
  );
  const lMonth =
    parseInt(londonDate.find((p) => p.type === "month")!.value, 10) - 1;
  const lDay = parseInt(londonDate.find((p) => p.type === "day")!.value, 10);

  const fridayLondonAsUTC = new Date(
    Date.UTC(lYear, lMonth, lDay + daysUntilFriday, 22, 0, 0, 0),
  );

  const checkParts = new Intl.DateTimeFormat("en-US", {
    timeZone: LONDON_TZ,
    hour: "numeric",
    hour12: false,
  }).formatToParts(fridayLondonAsUTC);
  const londonHourAtUTC = parseInt(
    checkParts.find((p) => p.type === "hour")!.value,
    10,
  );
  const offsetHours = (londonHourAtUTC === 24 ? 0 : londonHourAtUTC) - 22;
  const fridayOpenUTC = new Date(
    fridayLondonAsUTC.getTime() - offsetHours * 3600_000,
  );

  return Math.max(fridayOpenUTC.getTime() - now.getTime(), 1000);
}

function formatLocalizedReopenTime(reopenUTC: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(reopenUTC);
}

interface MarketsClosedStatus {
  isMarketsClosed: boolean;
  localizedReopenTime: string;
}

export function useMarketsClosedStatus(): MarketsClosedStatus {
  const [now, setNow] = useState(() => new Date());

  const isClosed = useMemo(() => isWithinWeekendWindow(now), [now]);

  const localizedReopenTime = useMemo(() => {
    if (!isClosed) return "";
    const reopenUTC = getNextSundayReopenUTC(now);
    return formatLocalizedReopenTime(reopenUTC);
  }, [isClosed, now]);

  useEffect(() => {
    const ms = msUntilNextTransition(now, isClosed);
    const timeout = setTimeout(() => setNow(new Date()), ms);
    return () => clearTimeout(timeout);
  }, [now, isClosed]);

  return { isMarketsClosed: isClosed, localizedReopenTime };
}
